import React from "react";
import { Lightbulb, FlaskConical, Package, Rocket, ArrowRight } from "lucide-react";

const STEPS = [
    {
        Icon: Lightbulb,
        label: "Ideation",
        body: "Identify and validate innovations with commercial potential.",
    },
    {
        Icon: FlaskConical,
        label: "R&D",
        body: "Apply biotechnology expertise to refine and scale solutions.",
    },
    {
        Icon: Package,
        label: "Product Development",
        body: "Transform prototypes into market-ready products.",
    },
    {
        Icon: Rocket,
        label: "Commercialization",
        body: "Launch products and establish sustainable business models.",
    },
];

export const Approach = () => {
    return (
        <section
            id="approach"
            data-testid="approach-section"
            className="section-pad relative overflow-hidden"
        >
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-moss))]/15"
                style={{ width: 560, height: 560, top: -120, right: -140 }}
            />
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-copper))]/10"
                style={{ width: 500, height: 500, bottom: -200, left: -160 }}
            />

            <div className="container-x relative">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
                    <div className="lg:col-span-7">
                        <p className="overline">
                            <span className="eyebrow-line" />
                            Our approach
                        </p>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.02]">
                            From spark to{" "}
                            <em className="not-italic text-primary">scale</em> —
                            <span className="block">a four-step journey.</span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-base md:text-lg leading-relaxed text-muted-foreground">
                        Every Yaalee engagement follows a disciplined path — rigorous at
                        each stage, patient about compounding value, relentless about
                        reaching the market.
                    </p>
                </div>

                {/* Horizontal step flow (desktop) */}
                <div className="hidden lg:block relative">
                    {/* Dashed connecting line */}
                    <div
                        aria-hidden
                        className="absolute top-10 left-[8%] right-[8%] h-px border-t border-dashed border-primary/40"
                    />

                    <div className="relative grid grid-cols-4 gap-6">
                        {STEPS.map(({ Icon, label, body }, i) => (
                            <div
                                key={label}
                                data-testid={`approach-step-${i}`}
                                className="group relative flex flex-col items-center text-center"
                            >
                                {/* Numbered bubble + icon */}
                                <div className="relative z-10 mb-7">
                                    <div className="h-20 w-20 rounded-full bg-background border border-border shadow-md shadow-primary/5 flex items-center justify-center transition-all group-hover:border-primary group-hover:shadow-primary/20 group-hover:-translate-y-1">
                                        <Icon className="h-7 w-7 text-primary transition-transform group-hover:scale-110" />
                                    </div>
                                    <span className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold flex items-center justify-center tabular-nums font-sans">
                                        0{i + 1}
                                    </span>
                                </div>

                                {/* Arrow on non-last */}
                                {i < STEPS.length - 1 && (
                                    <ArrowRight
                                        aria-hidden
                                        className="hidden lg:block absolute top-[34px] -right-4 h-4 w-4 text-primary/70"
                                    />
                                )}

                                <h3 className="font-display text-2xl font-medium tracking-tight">
                                    {label}
                                </h3>
                                <div className="mt-3 h-px w-10 bg-border group-hover:bg-primary group-hover:w-16 transition-all duration-500" />
                                <p className="mt-4 max-w-[240px] text-[14px] leading-relaxed text-muted-foreground">
                                    {body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Stacked timeline (tablet/mobile) */}
                <div className="lg:hidden relative">
                    <div
                        aria-hidden
                        className="absolute top-0 bottom-0 left-[38px] w-px border-l border-dashed border-primary/40"
                    />
                    <div className="space-y-8">
                        {STEPS.map(({ Icon, label, body }, i) => (
                            <div
                                key={label}
                                data-testid={`approach-step-m-${i}`}
                                className="relative flex gap-6 items-start"
                            >
                                <div className="relative shrink-0">
                                    <div className="h-[76px] w-[76px] rounded-full bg-background border border-border shadow-md shadow-primary/5 flex items-center justify-center">
                                        <Icon className="h-6 w-6 text-primary" />
                                    </div>
                                    <span className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center tabular-nums font-sans">
                                        0{i + 1}
                                    </span>
                                </div>
                                <div className="pt-3">
                                    <h3 className="font-display text-2xl font-medium tracking-tight">
                                        {label}
                                    </h3>
                                    <p className="mt-2 max-w-md text-[14px] leading-relaxed text-muted-foreground">
                                        {body}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                    {[
                        { k: "Idea → MVP", v: "Validated bench prototypes from day-one discovery" },
                        { k: "MVP → Product", v: "Scale-up, stability, certification — built in parallel" },
                        { k: "Product → Market", v: "Channel, brand, distribution — done with partners" },
                    ].map((s, i) => (
                        <div
                            key={i}
                            data-testid={`approach-stage-${i}`}
                            className="group rounded-[1.25rem] border border-border bg-card/60 backdrop-blur-sm p-6 md:p-7 hover:border-primary/50 transition-colors"
                        >
                            <div className="flex items-center justify-between">
                                <p className="font-display text-[20px] md:text-[22px] font-medium tracking-tight">
                                    {s.k}
                                </p>
                                <span className="h-2 w-2 rounded-full bg-primary group-hover:scale-150 transition-transform" />
                            </div>
                            <div className="mt-4 h-px w-10 bg-border group-hover:w-16 group-hover:bg-primary transition-all duration-500" />
                            <p className="mt-4 text-[13.5px] leading-relaxed text-muted-foreground">
                                {s.v}
                            </p>
                        </div>
                    ))}
                </div>

                <div className="mt-14 text-center">
                    <span className="inline-block text-[11px] uppercase tracking-[0.22em] text-muted-foreground divider-leaf">
                        Discipline · Patience · Impact
                    </span>
                </div>
            </div>
        </section>
    );
};

export default Approach;
